/**
 * OPERA OS session authentication client.
 * Credentials stay in HttpOnly cookies — never store passwords or session secrets in JS storage.
 */

import { apiUrl } from "@/lib/runtimeUrls";

let csrfToken = "";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  initials: string;
  avatarUrl?: string | null;
};

export type SessionResponse = {
  success: boolean;
  authenticated: boolean;
  csrfToken: string;
  setupRequired?: boolean;
  user?: AuthUser;
  error?: string;
};

function rememberCsrf(token?: string) {
  if (typeof token === "string" && token.trim() !== "") {
    csrfToken = token.trim();
  }
}

/** Root-relative /api/auth/uploads/* must use apex API host on app.cyberwebopera.com. */
export function resolveMediaUrl(path?: string | null): string {
  if (!path || !String(path).trim()) return "";
  return apiUrl(String(path).trim());
}

function normalizeUser(user?: AuthUser | null): AuthUser | undefined {
  if (!user) return undefined;
  return {
    ...user,
    avatarUrl: user.avatarUrl ? resolveMediaUrl(user.avatarUrl) : user.avatarUrl,
  };
}

export function getCsrfToken(): string {
  return csrfToken;
}

/** Always sync CSRF from the live session before privilege-changing POSTs. */
async function ensureFreshCsrf(): Promise<void> {
  await fetchSession();
}

export async function authFetch(
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    if (!headers.has("X-CSRF-Token") && csrfToken) {
      headers.set("X-CSRF-Token", csrfToken);
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }
  return fetch(apiUrl(input), {
    ...init,
    method,
    headers,
    credentials: "include",
    cache: "no-store",
  });
}

export async function fetchSession(): Promise<SessionResponse> {
  const res = await authFetch("/api/auth/session.php");
  const data = (await res.json()) as SessionResponse;
  rememberCsrf(data.csrfToken);
  return { ...data, user: normalizeUser(data.user) };
}

export async function loginRequest(
  email: string,
  password: string
): Promise<{ ok: boolean; status: number; user?: AuthUser; error?: string }> {
  // Always re-fetch session CSRF — soft redirects to login can leave a stale in-memory token
  // after session_regenerate_id / destroySession rotated the server token.
  await ensureFreshCsrf();
  const res = await authFetch("/api/auth/login.php", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json()) as {
    success?: boolean;
    user?: AuthUser;
    csrfToken?: string;
    error?: string;
  };
  rememberCsrf(data.csrfToken);
  if (!res.ok || !data.success || !data.user) {
    return { ok: false, status: res.status, error: data.error || "Invalid credentials." };
  }
  return { ok: true, status: res.status, user: normalizeUser(data.user) };
}

export async function logoutRequest(): Promise<void> {
  await ensureFreshCsrf();
  try {
    const res = await authFetch("/api/auth/logout.php", { method: "POST", body: "{}" });
    const data = (await res.json()) as { csrfToken?: string };
    rememberCsrf(data.csrfToken);
  } catch {
    // ignore network errors on logout
  }
}

export async function changePasswordRequest(input: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ ok: boolean; status: number; error?: string; requiresReauth?: boolean }> {
  await ensureFreshCsrf();
  const res = await authFetch("/api/auth/change-password.php", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as {
    success?: boolean;
    error?: string;
    requiresReauth?: boolean;
    csrfToken?: string;
  };
  rememberCsrf(data.csrfToken);
  if (!res.ok || !data.success) {
    return { ok: false, status: res.status, error: data.error || "Unable to change password." };
  }
  return { ok: true, status: res.status, requiresReauth: Boolean(data.requiresReauth) };
}

export async function changeEmailRequest(input: {
  currentPassword: string;
  newEmail: string;
  confirmEmail: string;
}): Promise<{ ok: boolean; status: number; error?: string; requiresReauth?: boolean }> {
  await ensureFreshCsrf();
  const res = await authFetch("/api/auth/change-email.php", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as {
    success?: boolean;
    error?: string;
    requiresReauth?: boolean;
    csrfToken?: string;
  };
  rememberCsrf(data.csrfToken);
  if (!res.ok || !data.success) {
    return { ok: false, status: res.status, error: data.error || "Unable to change email." };
  }
  return { ok: true, status: res.status, requiresReauth: Boolean(data.requiresReauth) };
}

export async function setupAdminRequest(input: {
  email: string;
  full_name?: string;
  password: string;
}): Promise<{ ok: boolean; status: number; error?: string }> {
  await ensureFreshCsrf();
  const res = await authFetch("/api/auth/setup.php", {
    method: "POST",
    body: JSON.stringify({
      email: input.email,
      full_name: input.full_name,
      setupPassword: input.password,
    }),
  });
  const data = (await res.json()) as { success?: boolean; error?: string };
  if (!res.ok || !data.success) {
    return { ok: false, status: res.status, error: data.error || "Setup failed." };
  }
  return { ok: true, status: res.status };
}

/** Headers for authenticated OPERA API calls (session cookie + CSRF). */
export function operaApiHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}
